import React from "react";
import {
  Modal,
  Box,
  Typography,
  Button,
  useMediaQuery,
  createTheme,
  Backdrop,
  CircularProgress,
  Divider,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import ArrowForwardIosSharpIcon from "@mui/icons-material/ArrowForwardIosSharp";
import MuiAccordion, { AccordionProps } from "@mui/material/Accordion";
import MuiAccordionSummary, { AccordionSummaryProps } from "@mui/material/AccordionSummary";
import MuiAccordionDetails from "@mui/material/AccordionDetails";
import { PrestacionHistorico } from "../../logic/domain/entities/prestacionHistorico";
import { LineChart, Line, CartesianGrid, XAxis, YAxis, Tooltip } from 'recharts';

interface FilterModalProps {
  open: boolean;
  onClose: () => void;
  prestacionHistorico: PrestacionHistorico[];
  loading: boolean; 
}

const Accordion = styled((props: AccordionProps) => (
  <MuiAccordion disableGutters elevation={0} square {...props} />
))(({ theme }) => ({
  border: `1px solid ${theme.palette.divider}`,
  '&:not(:last-child)': {
    borderBottom: 0,
  },
  '&::before': {
    display: 'none',
  },
}));

const AccordionSummary = styled((props: AccordionSummaryProps) => (
  <MuiAccordionSummary
    expandIcon={<ArrowForwardIosSharpIcon sx={{ fontSize: '0.9rem' }} />}
    {...props}
  />
))(({ theme }) => ({
  backgroundColor: 'rgba(0, 0, 0, .03)',
  flexDirection: 'row-reverse',
  '& .MuiAccordionSummary-expandIconWrapper.Mui-expanded': {
    transform: 'rotate(90deg)',
  },
  '& .MuiAccordionSummary-content': {
    marginLeft: theme.spacing(1),
  },
}));

const AccordionDetails = styled(MuiAccordionDetails)(({ theme }) => ({
  padding: theme.spacing(2),
  borderTop: '1px solid rgba(0, 0, 0, .125)',
  display: 'flex',
}));

const PrestacionModal: React.FC<FilterModalProps> = ({ open, onClose, prestacionHistorico, loading }) => {
  const theme = createTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("sm"));
  const [expanded, setExpanded] = React.useState<string | false>('panel1');

  const handleChange = (panel: string) => (event: React.SyntheticEvent, newExpanded: boolean) => {
    setExpanded(newExpanded ? panel : false);
  };
  return (
    <Modal open={open} onClose={onClose}>
      <div>
        <Backdrop open={loading} sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }}>
          <CircularProgress color="inherit" />
        </Backdrop>
        
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: isSmallScreen ? "90%" : 'auto', // Ajustado a 90% para pantallas pequeñas
            maxWidth: 800,
            bgcolor: "background.paper",
            p: isSmallScreen ? 2 : 4, // Reducir el padding en pantallas pequeñas
            boxShadow: 24,
            borderRadius: 3,
            overflow: "auto",
            maxHeight: "90vh",
          }}
        >
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: 2,
            }}
          >
            {prestacionHistorico.length === 0 ? (
              <Typography variant="h6" align="center">
                No hay datos disponibles
              </Typography>
            ) : (
              prestacionHistorico.map((prestacion, index) => {
                const historico = prestacion.getHistorico();
                const data = historico.map((item) => ({
                  fecha: item.fecha,
                  valor: item.valor,
                })).reverse();

                return (
                  <Accordion
                    key={index}
                    expanded={expanded === `panel${index + 1}`}
                    onChange={handleChange(`panel${index + 1}`)}
                  >
                    <AccordionSummary aria-controls={`panel${index + 1}d-content`} id={`panel${index + 1}d-header`}>
                      <Typography>{prestacion.getPrestacion()}</Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                      <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
                        <Box sx={{ flex: 1, paddingRight: 2 }}>
                          <Typography variant="h6" align="center" sx={{ marginBottom: 2 }}>
                            Gráfica de datos
                          </Typography>
                          <div style={{ height: '300px', width: '100%' }}>
                            <LineChart width={window.innerWidth < 600 ? window.innerWidth - 100 : 600} height={300} data={data} margin={{ top: 5, right: 20, bottom: 5, left: 0 }}>
                              <Line type="monotone" dataKey="valor" stroke="#00A3A8" />
                              <CartesianGrid stroke="#ccc" strokeDasharray="5 5" />
                              <XAxis dataKey="fecha" />
                              <YAxis />
                              <Tooltip />
                            </LineChart>
                          </div>
                        </Box>
                        <Divider sx={{ margin: '20px 0' }} /> {/* Divider con margen */}
                        <Box sx={{ flex: 1, overflow: 'auto' }}>
                          <Typography variant="h6" align="center" sx={{ marginBottom: 2 }}>Histórico de Datos</Typography>
                          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                            <thead>
                              <tr>
                                <th style={{ border: '1px solid #ccc', padding: '8px', backgroundColor: '#00A3A8', color: '#fff' }}>Fecha</th>
                                <th style={{ border: '1px solid #ccc', padding: '8px', backgroundColor: '#00A3A8', color: '#fff' }}>Muestra</th>
                                <th style={{ border: '1px solid #ccc', padding: '8px', backgroundColor: '#00A3A8', color: '#fff' }}>Valor</th>
                                <th style={{ border: '1px solid #ccc', padding: '8px', backgroundColor: '#00A3A8', color: '#fff' }}>Unidad</th>
                              </tr>
                            </thead>
                            <tbody>
                              {historico.map((item, idx) => (
                                <tr key={idx}>
                                  <td style={{ border: '1px solid #ccc', padding: '8px' }}>{item.fecha}</td>
                                  <td style={{ border: '1px solid #ccc', padding: '8px' }}>{item.muestra}</td>
                                  <td style={{ border: '1px solid #ccc', padding: '8px' }}>{item.valor}</td>
                                  <td style={{ border: '1px solid #ccc', padding: '8px' }}>{item.unidad}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </Box>
                      </Box>
                    </AccordionDetails>
                  </Accordion>
                );
              })
            )}
          </Box>
          <Box sx={{ display: "flex", justifyContent: "flex-end", padding:"10px" }}>
            <Button onClick={onClose} variant="contained">
              Cerrar
            </Button>
          </Box>
        </Box>
      </div>
    </Modal>
  );
};

export default PrestacionModal;
